/**
 * ApprovalsNavItem — the "Approvals" secondary-nav entry in the AI Agents module
 * sidebar, with a live ThreadUnreadBadge-style count of open approvals THIS user
 * may resolve. Rendered only inside the AI Agents nav, so its count query is
 * scoped to when that nav is on screen.
 */
import { useTranslation } from 'react-i18next';
import { ShieldCheck } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/ui/tooltip';
import { AppNavLink, BidiText, ThreadUnreadBadge } from '../../../shared/components/common';
import { NAV_ITEM_SELECTED, NAV_ITEM_UNSELECTED } from '../../../shared/styles/sidebar';
import { useInboxApprovalsCount } from '../hooks/useOctopusApi';

interface ApprovalsNavItemProps {
  showLabels: boolean;
  selected: boolean;
  /** Destination — an `href`, so this entry opens in a new tab like any other link. */
  path: string;
  /** Side effect once navigation has started in this window (closing the mobile drawer). */
  onNavigate?: () => void;
}

export function ApprovalsNavItem({ showLabels, selected, path, onNavigate }: ApprovalsNavItemProps) {
  const { t } = useTranslation('common');
  const { data: count } = useInboxApprovalsCount();
  const openCount = count ?? 0;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <AppNavLink
          to={path}
          onNavigate={onNavigate}
          className={cn(
            'flex items-center w-full rounded-md transition-all duration-200',
            showLabels ? 'gap-3 px-4 py-2' : 'relative justify-center px-2 py-2.5',
            selected ? NAV_ITEM_SELECTED : NAV_ITEM_UNSELECTED,
          )}
        >
          <span className="shrink-0 [&>svg]:h-4 [&>svg]:w-4">
            <ShieldCheck className="h-4 w-4" />
          </span>
          {showLabels ? (
            <>
              <BidiText className="text-sm truncate flex-1 text-start">
                {t('nav.aiAgents.approvals')}
              </BidiText>
              <ThreadUnreadBadge
                messageCount={openCount}
                unreadCount={openCount}
                ariaLabel={t('nav.aiAgents.approvalsPending', { count: openCount })}
              />
            </>
          ) : (
            openCount > 0 && (
              <span className="absolute top-1 right-1">
                <ThreadUnreadBadge
                messageCount={openCount}
                unreadCount={openCount}
                ariaLabel={t('nav.aiAgents.approvalsPending', { count: openCount })}
              />
              </span>
            )
          )}
        </AppNavLink>
      </TooltipTrigger>
      {!showLabels && (
        <TooltipContent side="right" className="max-w-xs">
          {t('nav.aiAgents.approvals')}
        </TooltipContent>
      )}
    </Tooltip>
  );
}
